import React, { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { isAdminUser } from "@/lib/roles";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, FileDown, Pencil, Trash2, Percent, ImageIcon, FileUp, AlertCircle, Eye, EyeOff } from "lucide-react";
import PortalAccessActions from "@/components/PortalAccessActions";
import PartnerAnalyticsPanel from "@/components/PartnerAnalyticsPanel";
import { toast } from "sonner";
import { downloadCsv } from "@/lib/exporters";
import { BulkActions, SelectionCheckbox, SelectAllCheckbox } from "@/components/BulkActions";
import { useSearchParams } from "react-router-dom";

const EMPTY = {
  name: "", company: "", email: "", phone: "",
  poc_name: "", poc_email: "", poc_phone: "",
  emergency_contact_name: "", emergency_contact_phone: "",
  dob: "", anniversaries_text: "",
  territory_zips_text: "",
  color: "#A0C4FF", icon_glyph: "🚚", photo_url: "",
  lat: "", lng: "", notes: "",
  role: "", statement: "",
  wholesale_discount_percent: "",
  stripe_account_id: "",
};

const PASTEL_OPTIONS = ["#FFB5A7","#FCD5CE","#FAE1DD","#FFD6A5","#FDFFB6","#CAFFBF","#9BF6FF","#A0C4FF","#BDB2FF","#FFC6FF","#D0F4DE"];
const GLYPH_OPTIONS = ["🚚", "✂️", "💇", "💅", "💄", "🎨", "🌟", "📦", "🏪", "🧴", "🪞", "🎯", "🛒", "🧑‍🎨", "🚗", "🦄", "🐝", "🌸", "🔥", "⚡"];

export default function EndpointPartnersPage() {
  const { user } = useAuth();
  const isAdmin = isAdminUser(user);
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedEditRef = useRef(false);
  const [partners, setPartners] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  const load = async () => setPartners(await api.get("/endpoint-partners").then(r => r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name || "", company: p.company || "", email: p.email || "", phone: p.phone || "",
      poc_name: p.poc_name || "", poc_email: p.poc_email || "", poc_phone: p.poc_phone || "",
      emergency_contact_name: p.emergency_contact_name || "", emergency_contact_phone: p.emergency_contact_phone || "",
      dob: p.dob || "",
      anniversaries_text: (p.anniversaries || []).map(a => `${a.label || ""}:${a.date || ""}`).join("\n"),
      territory_zips_text: (p.territory_zips || []).join(", "),
      color: p.color || "#A0C4FF",
      icon_glyph: p.icon_glyph || "🚚",
      photo_url: p.photo_url || "",
      lat: p.lat ?? "", lng: p.lng ?? "", notes: p.notes || "",
      role: p.role || "", statement: p.statement || "",
      wholesale_discount_percent: p.wholesale_discount_percent ?? p.commission_split_percent ?? "",
      stripe_account_id: p.stripe_account_id || "",
    });
    setOpen(true);
  };

  useEffect(() => {
    const editId = searchParams.get("edit");
    const partner = partners.find((p) => p.id === editId);
    if (partner && !requestedEditRef.current) {
      requestedEditRef.current = true;
      openEdit(partner);
      setSearchParams({}, { replace: true });
    }
  }, [partners, searchParams, setSearchParams]);

  // Upload a headshot straight to the object store — works during both the
  // "Add partner" (no id yet) and edit flows. The URL is saved on submit.
  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/endpoint-partners/photo-upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, photo_url: data.photo_url }));
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally { setUploadingPhoto(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      dob: form.dob || null,
      lat: form.lat === "" ? null : Number(form.lat),
      lng: form.lng === "" ? null : Number(form.lng),
      wholesale_discount_percent:
        form.wholesale_discount_percent === "" || form.wholesale_discount_percent === null
          ? null
          : Number(form.wholesale_discount_percent),
      stripe_account_id: form.stripe_account_id?.trim() || "",
      anniversaries: form.anniversaries_text.split("\n").map(s => s.trim()).filter(Boolean).map(s => {
        const [label, date] = s.split(":"); return { label: (label || "").trim(), date: (date || "").trim() };
      }),
      territory_zips: form.territory_zips_text.split(",").map(s => s.trim()).filter(Boolean),
    };
    delete payload.anniversaries_text; delete payload.territory_zips_text;
    try {
      if (editing) await api.put(`/endpoint-partners/${editing.id}`, payload);
      else await api.post("/endpoint-partners", payload);
      toast.success(editing ? "Partner updated" : "Partner added");
      setOpen(false); setEditing(null); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete distributing partner "${p.name}"? This cannot be undone.`)) return;
    try { await api.delete(`/endpoint-partners/${p.id}`); toast.success("Partner deleted"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  // Show/hide a partner on the public "Meet The Team" Essence roster.
  const toggleTeamVisibility = async (p) => {
    const hidden = !p.hidden_from_team;
    setPartners((prev) => prev.map((x) => (x.id === p.id ? { ...x, hidden_from_team: hidden } : x)));
    try {
      await api.post(`/admin/endpoint-partners/${p.id}/team-visibility`, { hidden });
      toast.success(hidden ? `${p.name} hidden from Essence` : `${p.name} shown on Essence`);
    } catch (err) {
      setPartners((prev) => prev.map((x) => (x.id === p.id ? { ...x, hidden_from_team: !hidden } : x)));
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="p-3 sm:p-6 lg:p-10 max-w-full">
      <header className="flex justify-between items-end mb-6 flex-wrap gap-3">
        <div>
          <p className="label-eyebrow">Distribution network</p>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">Distributing Partners</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => downloadCsv("/endpoint-partners/export.csv", "endpoint_partners.csv")} data-testid="export-partners-csv"><FileDown size={14} className="mr-1" /> Export</Button>
          <label className="inline-flex">
            <input type="file" accept=".csv" className="hidden" data-testid="import-partners-csv"
              onChange={async (e) => { const f = e.target.files?.[0]; if (!f) return; const fd = new FormData(); fd.append("file", f);
                try { const { data } = await api.post("/endpoint-partners/import.csv", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success(`Imported ${data.created} partners`); load(); }
                catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } e.target.value = ""; }} />
            <span className="inline-flex items-center cursor-pointer px-4 py-2 border border-[var(--border)] rounded-md text-sm font-medium hover:bg-[var(--bg-soft)]">Import CSV</span>
          </label>
          <Button className="btn-brand" onClick={openNew} data-testid="add-partner-button"><Plus size={14} className="mr-1" /> Partner</Button>
        </div>
      </header>

      <div className="mb-3 flex items-center gap-2 text-sm text-gray-600"><SelectAllCheckbox records={partners} selectedIds={selectedIds} setSelectedIds={setSelectedIds} /> Select all visible</div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {partners.map(p => (
          <Card key={p.id} className="p-5" data-testid={`partner-card-${p.id}`}>
            <div className="flex justify-end"><SelectionCheckbox checked={selectedIds.includes(p.id)} onChange={(checked) => setSelectedIds(checked ? [...selectedIds, p.id] : selectedIds.filter((id) => id !== p.id))} testId={`select-row-checkbox-${p.id}`} label={`Select ${p.name}`} /></div>
            <div className="flex items-center gap-3 mb-2">
              {p.photo_url ? (
                <img src={p.photo_url} alt={p.name}
                     className="w-12 h-12 rounded-full object-cover border-2"
                     style={{ borderColor: p.color }}
                     data-testid={`partner-photo-${p.id}`} />
              ) : (
                <span className="w-4 h-4 rounded-full border" style={{ background: p.color }} />
              )}
              <div className="min-w-0">
                <h3 className="font-semibold text-lg capitalize truncate">{p.name}</h3>
                {p.role && <p className="text-xs uppercase tracking-widest text-[var(--brand)] font-semibold" data-testid={`partner-role-${p.id}`}>{p.role}</p>}
              </div>
              {p.documents_incomplete && (
                <span
                  className="ml-auto shrink-0 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold text-red-800 bg-red-100 border border-red-200 px-1.5 py-0.5 rounded"
                  title="Driver's license or insurance is missing or unverified"
                  data-testid={`partner-docs-incomplete-${p.id}`}
                >
                  <AlertCircle size={11} /> Docs incomplete
                </span>
              )}
            </div>
            <p className="text-sm text-gray-500 capitalize">{p.company}</p>
            <dl className="mt-3 space-y-1 text-sm">
              <div><span className="label-eyebrow">POC </span><span className="capitalize">{p.poc_name || "—"}</span></div>
              <div><span className="label-eyebrow">Phone </span>{p.poc_phone || p.phone || "—"}</div>
              <div><span className="label-eyebrow">Emergency </span><span className="capitalize">{p.emergency_contact_name || "—"}</span> {p.emergency_contact_phone}</div>
              <div><span className="label-eyebrow">DOB </span>{p.dob || "—"}</div>
              <div><span className="label-eyebrow">Territory </span>{(p.territory_zips || []).join(", ") || "—"}</div>
              {isAdmin && (
                <div className="pt-2 mt-2 border-t border-dashed border-amber-200 flex items-center gap-1.5" data-testid={`partner-commission-${p.id}`}>
                  <Percent size={11} className="text-amber-600" />
                  <span className="label-eyebrow text-amber-800">Wholesale Discount</span>
                  <span className="ml-auto text-base font-bold tabular-nums text-amber-800">
                    {p.wholesale_discount_percent != null || p.commission_split_percent != null
                      ? `${Number(p.wholesale_discount_percent ?? p.commission_split_percent).toFixed(1)}%`
                      : <span className="text-xs font-medium text-gray-400 italic">Not set</span>}
                  </span>
                </div>
              )}
            </dl>
            {isAdmin && p.portal_url && (
              <PortalAccessActions portalUrl={`${window.location.origin}${p.portal_url}`} name={p.name} testPrefix={`partner-${p.id}-portal`} compact />
            )}
            {isAdmin && p.id !== "warehouse" && <PartnerAnalyticsPanel partnerId={p.id} />}
            {p.id !== "warehouse" && (
              <div className="flex gap-2 mt-4">
                <Button variant="outline" size="sm" onClick={() => openEdit(p)} data-testid={`edit-partner-${p.id}`}><Pencil size={12} className="mr-1" /> Edit</Button>
                <Button variant="outline" size="sm" onClick={() => remove(p)} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`delete-partner-${p.id}`}><Trash2 size={12} className="mr-1" /> Delete</Button>
                {isAdmin && (
                  <button
                    type="button"
                    onClick={() => toggleTeamVisibility(p)}
                    title={p.hidden_from_team ? "Hidden from the Essence page — click to show" : "Shown on the Essence page — click to hide"}
                    className={`ml-auto inline-flex items-center justify-center w-9 h-9 rounded-md border transition-colors ${p.hidden_from_team ? "border-gray-300 text-gray-400 hover:bg-gray-50" : "border-emerald-300 text-emerald-600 hover:bg-emerald-50"}`}
                    data-testid={`toggle-partner-visibility-${p.id}`}
                  >
                    {p.hidden_from_team ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                )}
              </div>
            )}
          </Card>
        ))}
      </div>
      <BulkActions section="endpoint_partners" records={partners} selectedIds={selectedIds} setSelectedIds={setSelectedIds} onComplete={load} />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit partner" : "New distributing partner"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="grid grid-cols-2 gap-3" data-testid="partner-form">
            <div><Label>Name *</Label><Input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="partner-name" /></div>
            <div><Label>Company</Label><Input value={form.company} onChange={e => setForm({ ...form, company: e.target.value })} /></div>
            <div className="col-span-2 grid grid-cols-2 gap-3">
              <div>
                <Label>Role / Title <span className="text-[10px] text-gray-500 font-normal">(shown on Meet The Team)</span></Label>
                <Input value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}
                       placeholder="e.g. Distribution Manager, Regional Lead"
                       data-testid="partner-role" />
              </div>
              <div className="col-span-2">
                <Label>Edification statement <span className="text-[10px] text-gray-500 font-normal">(1–2 sentences shown on Meet The Team; leave blank for an AI quote)</span></Label>
                <Textarea value={form.statement} onChange={e => setForm({ ...form, statement: e.target.value })}
                          rows={2} placeholder="A personal motto or righteous ideology quote…"
                          data-testid="partner-statement" />
              </div>
              {isAdmin && (
                <div className="space-y-3">
                  <Label className="flex items-center gap-1.5">
                    Wholesale Discount % <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200">HQ only</span>
                  </Label>
                  <Input
                    type="number" min="0" max="100" step="0.5"
                    value={form.wholesale_discount_percent ?? ""}
                    onChange={e => setForm({ ...form, wholesale_discount_percent: e.target.value })}
                    placeholder="e.g. 15"
                    data-testid="partner-commission-input"
                  />
                  <p className="text-[10px] text-gray-500 mt-1">Discount applies in Wholesale Club Only</p>
                  <div>
                    <Label htmlFor="partner-stripe-account">Stripe Connect account ID <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200">HQ only</span></Label>
                    <Input id="partner-stripe-account" value={form.stripe_account_id || ""} onChange={e => setForm({ ...form, stripe_account_id: e.target.value })} placeholder="acct_..." data-testid="partner-stripe-account-input" />
                  </div>
                </div>
              )}
            </div>
            <div><Label>Email</Label><Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></div>
            <div><Label>POC Name</Label><Input value={form.poc_name} onChange={e => setForm({ ...form, poc_name: e.target.value })} /></div>
            <div><Label>POC Email</Label><Input value={form.poc_email} onChange={e => setForm({ ...form, poc_email: e.target.value })} /></div>
            <div><Label>POC Phone</Label><Input value={form.poc_phone} onChange={e => setForm({ ...form, poc_phone: e.target.value })} /></div>
            <div><Label>DOB</Label><Input type="date" value={form.dob} onChange={e => setForm({ ...form, dob: e.target.value })} data-testid="partner-dob" /></div>
            <div><Label>Emergency contact name</Label><Input value={form.emergency_contact_name} onChange={e => setForm({ ...form, emergency_contact_name: e.target.value })} /></div>
            <div><Label>Emergency contact phone</Label><Input value={form.emergency_contact_phone} onChange={e => setForm({ ...form, emergency_contact_phone: e.target.value })} /></div>
            <div className="col-span-2"><Label>Special anniversaries (one per line · Label:YYYY-MM-DD)</Label>
              <textarea className="w-full border rounded p-2 text-sm" rows={3}
                        value={form.anniversaries_text} onChange={e => setForm({ ...form, anniversaries_text: e.target.value })}
                        placeholder="Partner since:2023-01-15" data-testid="partner-anniversaries" /></div>
            <div className="col-span-2"><Label>Territory zip codes (comma-separated) *</Label>
              <Input value={form.territory_zips_text} onChange={e => setForm({ ...form, territory_zips_text: e.target.value })}
                     placeholder="10001, 10002, 10003" data-testid="partner-zips" /></div>
            <div><Label>Latitude</Label><Input value={form.lat} onChange={e => setForm({ ...form, lat: e.target.value })} /></div>
            <div><Label>Longitude</Label><Input value={form.lng} onChange={e => setForm({ ...form, lng: e.target.value })} /></div>
            <div className="col-span-2"><Label>Territory color</Label>
              <div className="flex gap-2 flex-wrap mt-1">
                {PASTEL_OPTIONS.map(c => (
                  <button type="button" key={c} onClick={() => setForm({ ...form, color: c })}
                          className={`w-7 h-7 rounded-full border-2 ${form.color === c ? "border-gray-800" : "border-white"}`}
                          style={{ background: c }} data-testid={`color-${c}`} />
                ))}
              </div>
            </div>

            {/* Marker preview + icon controls */}
            <div className="col-span-2 border border-[var(--border)] rounded-lg p-3 bg-[var(--bg-soft)]" data-testid="partner-marker-panel">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-14 h-14 rounded-full flex items-center justify-center text-2xl border-2 border-white overflow-hidden"
                     style={{ background: form.color, boxShadow: `0 0 0 2px ${form.color},0 2px 6px rgba(0,0,0,.2)` }}
                     data-testid="marker-preview">
                  {form.photo_url ? (
                    <img src={form.photo_url} alt="Headshot" className="w-full h-full object-cover"
                         onError={(e) => { e.currentTarget.style.display = "none"; }} />
                  ) : (form.icon_glyph || "🚚")}
                </div>
                <div className="text-sm">
                  <div className="font-semibold">Field-map marker preview</div>
                  <div className="text-xs text-gray-500">Photo overrides the glyph when set.</div>
                </div>
              </div>
              <Label className="text-xs">Pick an icon glyph</Label>
              <div className="flex gap-1 flex-wrap mt-1 mb-3">
                {GLYPH_OPTIONS.map(g => (
                  <button type="button" key={g} onClick={() => setForm({ ...form, icon_glyph: g })}
                          className={`w-8 h-8 rounded-md text-lg flex items-center justify-center border ${form.icon_glyph === g ? "border-[var(--brand)] bg-white ring-2 ring-[var(--brand)]" : "border-[var(--border)] bg-white hover:border-gray-500"}`}
                          data-testid={`glyph-${g}`}>{g}</button>
                ))}
              </div>
              <Label className="text-xs">Upload a headshot / logo</Label>
              <label className="mt-1 flex flex-col items-center justify-center gap-1.5 w-full px-4 py-5 border-2 border-dashed border-[var(--border)] rounded-lg cursor-pointer hover:border-[var(--brand)]/50 hover:bg-white transition-colors text-center">
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  className="hidden"
                  onChange={uploadPhoto}
                  data-testid="partner-photo-upload-input"
                />
                <FileUp size={20} className={uploadingPhoto ? "text-gray-400 animate-pulse" : "text-[var(--brand)]"} />
                <span className="text-sm font-medium" data-testid="partner-photo-upload-button">
                  {uploadingPhoto ? "Uploading…" : (form.photo_url ? "Replace photo" : "Upload from computer or phone")}
                </span>
                <span className="text-[11px] text-gray-400">JPG, PNG, GIF or WebP · up to 15 MB</span>
              </label>
              {form.photo_url && (
                <button type="button" onClick={() => setForm({ ...form, photo_url: "" })}
                        className="text-xs text-red-600 hover:underline mt-1" data-testid="clear-photo-url">
                  Remove headshot — use glyph instead
                </button>
              )}
            </div>

            <div className="col-span-2"><Label>Notes</Label><Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
            <Button className="btn-brand col-span-2" data-testid="partner-submit">{editing ? "Save changes" : "Add partner"}</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
